import { ApiError, BaseService } from "./BaseService";
import {
  ClassifiedWhatsAppSendError,
  isReengagementToken,
  SendTemplatePayload,
  SendTextPayload,
  WhatsAppConversation,
  WhatsAppMessage,
  WhatsAppTemplate,
} from "@/types/whatsapp";

class WhatsappService extends BaseService {
  private basePath(hotelId: string): string {
    return `/hotels/${hotelId}/whatsapp`;
  }

  /**
   * Free-text send. May be refused with `409` + a bare reason token
   * (`WHATSAPP_NO_CONVERSATION` | `WINDOW_CLOSED` | `WHATSAPP_DISABLED`
   * | `KILL_SWITCH` | recoverable Meta tokens);
   * the UI branches on it, so the automatic error toast is suppressed here.
   *
   * A `200` can still carry `status: 'FAILED'` (Meta rejected) — callers must read
   * `status`/`errorCode`, not assume delivery.
   */
  sendText(
    hotelId: string,
    conversationId: string,
    body: SendTextPayload,
  ): Promise<WhatsAppMessage> {
    return this.request<WhatsAppMessage>(
      `${this.basePath(hotelId)}/conversations/${conversationId}/messages/text`,
      { method: "POST", body: JSON.stringify(body), disableToast: true },
    );
  }

  listApprovedTemplates(hotelId: string): Promise<WhatsAppTemplate[]> {
    return this.get<WhatsAppTemplate[]>(`${this.basePath(hotelId)}/templates`, {
      status: "APPROVED",
    });
  }

  /**
   * Send an approved template. May be refused with `409` + a bare reason token
   * (`TEMPLATE_NOT_APPROVED` | `WHATSAPP_TEMPLATE_NOT_FOUND` | `REENGAGEMENT_DISABLED`
   * | `WHATSAPP_DISABLED` | `KILL_SWITCH`); handled in the UI.
   */
  sendTemplate(
    hotelId: string,
    conversationId: string,
    body: SendTemplatePayload,
  ): Promise<WhatsAppMessage> {
    return this.request<WhatsAppMessage>(
      `${this.basePath(hotelId)}/conversations/${conversationId}/messages/templates`,
      { method: "POST", body: JSON.stringify(body), disableToast: true },
    );
  }

  getConversation(
    hotelId: string,
    conversationId: string,
  ): Promise<WhatsAppConversation> {
    return this.get<WhatsAppConversation>(
      `${this.basePath(hotelId)}/conversations/${conversationId}`,
    );
  }

  downloadMessageMedia(
    hotelId: string,
    conversationId: string,
    messageId: string,
  ): Promise<Blob> {
    return this.request<Blob>(
      `${this.basePath(hotelId)}/conversations/${conversationId}/messages/${messageId}/media`,
      { method: "GET", responseType: "blob", disableToast: true },
    );
  }

  /**
   * Resolve-or-create the WhatsApp conversation backing a booking. Creates the
   * row when the guest has never messaged (derives the phone from the booking)
   * and stamps the Stream-channel link. Lets the re-engagement picker get a
   * conversation id for a first-contact template. `404` = booking unknown or
   * guest has no usable WhatsApp number; surfaced inline, so toast suppressed.
   */
  resolveConversation(
    hotelId: string,
    body: { bookingId: string; streamChannelId?: string },
  ): Promise<WhatsAppConversation> {
    return this.request<WhatsAppConversation>(
      `${this.basePath(hotelId)}/conversations/resolve`,
      { method: "POST", body: JSON.stringify(body), disableToast: true },
    );
  }
}

export const whatsappService = new WhatsappService();

/**
 * Classifies a WhatsApp send error by HTTP status + bare token so callers can branch
 * by `kind` instead of substring-parsing. See `ClassifiedWhatsAppSendError`.
 * - `409` + re-engagement token → `reengage` (open the template picker)
 * - any other `409` → `refusal` (inline error, no picker)
 * - `502` → `upstream` (integration down — never the picker)
 * - anything else → `unknown` (generic error)
 */
export const classifySendError = (
  error: unknown,
): ClassifiedWhatsAppSendError => {
  if (!(error instanceof ApiError)) {
    return { kind: "unknown", token: "", status: 0 };
  }
  const { message: token, statusCode: status } = error;
  if (status === 409) {
    return {
      kind: isReengagementToken(token) ? "reengage" : "refusal",
      token,
      status,
    };
  }
  if (status === 502) {
    return { kind: "upstream", token, status };
  }
  return { kind: "unknown", token, status };
};
